/**
 * Polymarket API client
 * gamma-api.polymarket.com + clob.polymarket.com
 * Всегда /api/gamma, /api/clob — проксируются (Vite dev/preview, nginx на EC2)
 */

const GAMMA_BASE = '/api/gamma'
const CLOB_BASE = '/api/clob'
// Same-origin через nginx proxy (см. deploy/polystrike-visual.nginx /api/data/).
// Раньше был прямой https://data-api.polymarket.com — упирался в CSP connect-src
// 'self', из-за чего useRedeemablePositions/usePortfolioValue молча возвращали
// null (TypeError: Failed to fetch), и портфель в StreakBar показывал только
// cash, а RedeemPill не появлялась.
const DATA_API_ORIGIN = '/api/data'

const FETCH_TIMEOUT_MS = 20_000

async function safeJson<T>(res: Response, context: string): Promise<T> {
  const text = await res.text()
  const ct = res.headers.get('content-type') ?? ''
  if (!ct.includes('application/json') || text.trimStart().startsWith('<')) {
    console.error(`[API Error] context=${context} url=${res.url} status=${res.status} ct="${ct}" body_start="${text.slice(0, 120)}"`)
    throw new Error(
      `${context}: получен HTML вместо JSON (прокси не сработал?). Перезапустите dev-сервер (npm run dev).`
    )
  }
  return JSON.parse(text) as T
}

function devFetch(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timeout))
}

/** Повтор запроса при 408/502/503 или при AbortError (клиентский таймаут). */
async function fetchWithRetry(
  url: string,
  options: { maxRetries?: number } = {}
): Promise<Response> {
  const { maxRetries = 2 } = options
  const fetcher = () => devFetch(url)
  let lastError: unknown = null
  let lastRes: Response | null = null
  for (let i = 0; i <= maxRetries; i++) {
    try {
      const res = await fetcher()
      if (res.status === 408 || res.status === 502 || res.status === 503) {
        lastRes = res
        if (i < maxRetries) await new Promise((r) => setTimeout(r, 1500 * (i + 1)))
        else break
      }
      return res
    } catch (e) {
      lastError = e
      if ((e instanceof Error && e.name === 'AbortError') && i < maxRetries) {
        await new Promise((r) => setTimeout(r, 1500 * (i + 1)))
      } else throw e
    }
  }
  if (lastRes) return lastRes
  throw lastError ?? new Error('Request failed')
}


export interface MarketOutcome {
  token_id: string
  outcome: string
  price: number
}

export interface PolymarketMarket {
  id: string
  question: string
  condition_id: string
  slug: string
  outcomes: string
  outcome_prices: string
  volume: number
  active: boolean
  closed: boolean
  enable_order_book: boolean
  order_type: 'GTC' | 'GTD'
  end_date_iso: string
  groupItemTitle: string | null
  groupItemThreshold: number | null
  tokens: Array<{
    token_id: string
    outcome: string
    price: string
  }>
}

export interface DailyRangeMarket {
  id: string
  question: string
  slug: string
  conditionId: string
  tokens: MarketOutcome[]
  endDate: string
  resolutionSource: string
  groupItemTitle?: string
}

export interface GammaMarket {
  id: string
  question: string
  slug: string
  /** API может вернуть строку JSON "[\"id1\",\"id2\"]" или массив */
  clobTokenIds: string | string[]
  outcomePrices: string | string[]
  bestBid?: number
  bestAsk?: number
  groupItemTitle?: string
  groupItemThreshold?: string
  acceptingOrders?: boolean
  closed?: boolean
  orderPriceMinTickSize?: number
  negRisk?: boolean
}

export interface GammaEvent {
  id: string
  slug: string
  title: string
  markets: GammaMarket[]
  endDate?: string
}

export async function fetchMarkets(params: {
  tag?: string
  closed?: boolean
  active?: boolean
  limit?: number
}): Promise<PolymarketMarket[]> {
  const search = new URLSearchParams()
  if (params.tag) search.set('tag', params.tag)
  if (params.closed !== undefined) search.set('closed', String(params.closed))
  if (params.active !== undefined) search.set('active', String(params.active))
  if (params.limit) search.set('limit', String(params.limit))

  const url = `${GAMMA_BASE}/markets?${search}`
  const res = await fetchWithRetry(url)
  if (!res.ok) throw new Error(`Gamma API: ${res.status}`)
  return res.json()
}

export async function fetchMarketBySlug(slug: string): Promise<PolymarketMarket | null> {
  const url = `${GAMMA_BASE}/markets/${slug}`
  const res = await fetchWithRetry(url)
  if (!res.ok) return null
  return safeJson<PolymarketMarket | null>(res, 'fetchMarketBySlug')
}

export async function fetchEvents(params: {
  tag?: string
  closed?: boolean
  limit?: number
  slug?: string
  active?: boolean
}): Promise<GammaEvent[]> {
  const search = new URLSearchParams()
  if (params.tag) search.set('tag', params.tag)
  if (params.closed !== undefined) search.set('closed', String(params.closed))
  if (params.active !== undefined) search.set('active', String(params.active))
  if (params.limit) search.set('limit', String(params.limit))
  if (params.slug) search.set('slug', params.slug)

  const url = `${GAMMA_BASE}/events?${search}`
  const res = await fetchWithRetry(url)
  if (!res.ok) throw new Error(`Gamma API events: ${res.status}`)
  return safeJson<GammaEvent[]>(res, 'fetchEvents')
}

/** Полное событие с markets: GET /events/slug/{slug} (надёжнее, чем список) */
export async function fetchEventBySlug(slug: string): Promise<GammaEvent | null> {
  const url = `${GAMMA_BASE}/events/slug/${encodeURIComponent(slug)}`
  const res = await fetchWithRetry(url)
  if (!res.ok) return null
  return safeJson<GammaEvent | null>(res, 'fetchEventBySlug')
}

/** Загрузить событие daily range для даты. Пробует несколько slug-вариантов. Пропускает уже зарезолвленные (endDate в прошлом). */
export async function fetchEventForDay(
  _cryptoSlug: string,
  _date: Date,
  slugsToTry: string[]
): Promise<GammaEvent | null> {
  const now = Date.now()
  for (const slug of slugsToTry) {
    const event = await fetchEventBySlug(slug)
    if (!event?.markets?.length) continue
    if (event.endDate && new Date(event.endDate).getTime() < now) continue
    return event
  }
  return null
}

export interface PricePoint {
  price: number
  size: number
}

function parsePricePayload(data: unknown, side: 'BUY' | 'SELL'): number | null {
  if (data == null || typeof data !== 'object') return null
  const o = data as Record<string, unknown>
  const p = o.price ?? o[side]
  if (p == null) return null
  const n = typeof p === 'string' ? parseFloat(p) : Number(p)
  return isNaN(n) ? null : n
}

/** Fetch midpoint from CLOB /midpoint endpoint */
export async function fetchMidpoint(tokenId: string): Promise<number | null> {
  try {
    const res = await fetchWithRetry(`${CLOB_BASE}/midpoint?token_id=${encodeURIComponent(tokenId)}`)
    if (!res.ok) return null
    const data = await safeJson<{ mid?: string | number }>(res, 'fetchMidpoint')
    const v = typeof data.mid === 'string' ? parseFloat(data.mid) : Number(data.mid ?? NaN)
    return isNaN(v) ? null : v
  } catch {
    return null
  }
}

/** Best bid and ask for a token. Uses /price, fallback to /midpoint. Never /book (broken for neg-risk). */
export async function fetchBestBidAsk(tokenId: string): Promise<{ bid: number; ask: number; mid: number | null }> {
  const tokenShort = tokenId.slice(0, 20) + '…'
  try {
    const [buyRes, sellRes] = await Promise.all([
      fetchWithRetry(`${CLOB_BASE}/price?token_id=${encodeURIComponent(tokenId)}&side=BUY`),
      fetchWithRetry(`${CLOB_BASE}/price?token_id=${encodeURIComponent(tokenId)}&side=SELL`),
    ])
    if (buyRes.ok && sellRes.ok) {
      const buyData = await safeJson<unknown>(buyRes, 'fetchBestBidAsk BUY')
      const sellData = await safeJson<unknown>(sellRes, 'fetchBestBidAsk SELL')
      const bid = parsePricePayload(buyData, 'BUY')
      const ask = parsePricePayload(sellData, 'SELL')
      if (bid != null && ask != null) {
        const mid = (bid + ask) / 2
        return { bid, ask, mid }
      }
    }
  } catch (_) { /* /price unavailable, try midpoint */ }
  const midpoint = await fetchMidpoint(tokenId)
  if (midpoint != null) return { bid: midpoint, ask: midpoint, mid: midpoint }
  throw new Error(`No price data for token ${tokenShort}`)
}


/**
 * Batch fetch mid prices via POST /midpoints (single request for all tokens).
 * Falls back to throttled individual GET /midpoint if batch fails.
 */
export async function fetchBatchMidPrices(tokenIds: string[]): Promise<Map<string, number>> {
  if (!tokenIds.length) return new Map()
  const result = new Map<string, number>()

  // Try batch endpoint first: POST /midpoints with BookParams[] format
  try {
    const res = await devFetch(`${CLOB_BASE}/midpoints`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tokenIds.map((id) => ({ token_id: id }))),
    })
    if (res.ok) {
      const data = await safeJson<Record<string, string>>(res, 'fetchBatchMidPrices')
      if (data && typeof data === 'object') {
        for (const [tokenId, midStr] of Object.entries(data)) {
          const v = parseFloat(midStr)
          if (Number.isFinite(v) && v > 0) result.set(tokenId, v)
        }
        if (result.size > 0) return result
      }
    }
    // 403 = rate limited — do NOT fall back to individual requests (would make it worse)
    if (res.status === 403) {
      console.warn('[fetchBatchMidPrices] 403 rate limited, skipping fallback')
      return result
    }
  } catch { /* batch failed, fall back to individual */ }

  // Fallback: individual requests, throttled (max 8 concurrent)
  // Only used when batch endpoint returns non-403 errors (400, 500, timeout)
  const CHUNK = 8
  for (let i = 0; i < tokenIds.length; i += CHUNK) {
    const chunk = tokenIds.slice(i, i + CHUNK)
    const entries = await Promise.all(
      chunk.map(async (tokenId) => [tokenId, await fetchMidpoint(tokenId)] as const),
    )
    for (const [tokenId, mid] of entries) {
      if (mid != null) result.set(tokenId, mid)
    }
  }
  return result
}

function toPricePoint(p: { price?: string | number; size?: string | number }): PricePoint {
  const price = typeof p.price === 'string' ? parseFloat(p.price) : Number(p.price ?? 0)
  const size = typeof p.size === 'string' ? parseFloat(p.size) : Number(p.size ?? 0)
  return { price: isNaN(price) ? 0 : price, size: isNaN(size) ? 0 : size }
}

export async function fetchOrderBook(tokenId: string): Promise<{
  bids: PricePoint[]
  asks: PricePoint[]
}> {
  const url = `${CLOB_BASE}/book?token_id=${tokenId}`
  const res = await fetchWithRetry(url)
  if (!res.ok) throw new Error(`CLOB API: ${res.status}`)
  const data = await safeJson<{ bids?: Array<{ price?: string | number; size?: string | number }>; asks?: Array<{ price?: string | number; size?: string | number }> }>(res, 'fetchOrderBook')
  const bids = (data.bids ?? []).map(toPricePoint)
  const asks = (data.asks ?? []).map(toPricePoint)
  return { bids, asks }
}

export async function fetchOrder(id: string): Promise<unknown> {
  const url = `${CLOB_BASE}/order/${id}`
  const res = await fetchWithRetry(url)
  if (!res.ok) throw new Error(`CLOB API: ${res.status}`)
  return safeJson<unknown>(res, 'fetchOrder')
}

/**
 * Data API: total position value for a user (portfolio value in USDC).
 * GET /value?user={address} → [{ user, value }], value in USDC.
 * See https://docs.polymarket.com/developers/misc-endpoints/data-api-get-positions
 */
export async function fetchPortfolioValue(userAddress: string): Promise<number | null> {
  if (!userAddress || !userAddress.startsWith('0x')) return null
  try {
    // Один и тот же способ для мобильного и десктопа: прямой вызов Data API (публичный, CORS разрешён).
    const url = `${DATA_API_ORIGIN}/value?user=${encodeURIComponent(userAddress)}`
    const res = await fetchWithRetry(url)
    const text = await res.text()
    let parsed: unknown = null
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = text.slice(0, 200)
    }
    if (!res.ok) return null
    const ct = res.headers.get('content-type') ?? ''
    if (!ct.includes('application/json') || (typeof parsed === 'string')) {
      throw new Error('fetchPortfolioValue: non-JSON response')
    }
    const data = parsed as Array<{ user?: string; value?: number }>
    const item = Array.isArray(data) ? data[0] : (data as { user?: string; value?: number } | null)
    const v = item?.value
    return typeof v === 'number' && !Number.isNaN(v) ? v : (Array.isArray(data) && data.length === 0 ? 0 : null)
  } catch {
    return null
  }
}

/** Data API position: outcome token position for a user. */
export interface Position {
  asset: string
  size: number
  avgPrice: number
}

/** Redeemable position (curPrice === 1 = winning). */
export interface RedeemablePosition {
  conditionId: string
  asset: string
  size: number
  curPrice: number
  outcome: string
  outcomeIndex?: number
  title?: string
  negRisk?: boolean
}

/**
 * Data API: redeemable positions (resolved, ready to claim).
 * GET /positions?user=X&redeemable=true, filter curPrice === 1.
 */
export async function fetchRedeemablePositions(userAddress: string): Promise<RedeemablePosition[]> {
  if (!userAddress || !userAddress.startsWith('0x')) return []
  try {
    const url = `${DATA_API_ORIGIN}/positions?user=${encodeURIComponent(userAddress)}&redeemable=true&limit=500`
    const res = await fetchWithRetry(url)
    if (!res.ok) return []
    const data = await res.json()
    if (!Array.isArray(data)) return []
    return data
      .filter((p: { redeemable?: boolean; curPrice?: number }) => p.redeemable === true && p.curPrice === 1)
      .map((p: {
        conditionId?: string
        asset?: string
        size?: number
        curPrice?: number
        outcome?: string
        outcomeIndex?: number
        title?: string
        negRisk?: boolean
        negativeRisk?: boolean
      }) => ({
        conditionId: (p.conditionId ?? '').toString(),
        asset: p.asset ?? '',
        size: typeof p.size === 'number' ? p.size : 0,
        curPrice: typeof p.curPrice === 'number' ? p.curPrice : 1,
        outcome: p.outcome ?? '',
        outcomeIndex: p.outcomeIndex,
        title: p.title,
        negRisk: p.negRisk ?? p.negativeRisk ?? false,
      }))
      .filter((p: RedeemablePosition) => p.conditionId)
  } catch {
    return []
  }
}

/**
 * Data API: current positions for a user.
 * GET /positions?user={address} → Position[]
 */
export async function fetchPositions(userAddress: string): Promise<Position[]> {
  if (!userAddress || !userAddress.startsWith('0x')) return []
  try {
    const url = `${DATA_API_ORIGIN}/positions?user=${encodeURIComponent(userAddress)}&limit=500`
    const res = await fetchWithRetry(url)
    if (!res.ok) return []
    const data = await res.json()
    if (!Array.isArray(data)) return []
    return data.map((p: { asset?: string; size?: number; avgPrice?: number; initialValue?: number }) => {
      const size = typeof p.size === 'number' ? p.size : 0
      let avgPrice = typeof p.avgPrice === 'number' ? p.avgPrice : 0
      // PM Data API sometimes returns avgPrice=0 for fresh positions.
      // Fallback: compute from initialValue/size if available.
      if (avgPrice === 0 && size > 0 && typeof p.initialValue === 'number' && p.initialValue > 0) {
        avgPrice = p.initialValue / size
      }
      return { asset: p.asset ?? '', size, avgPrice }
    }).filter((p: Position) => p.asset)
  } catch {
    return []
  }
}
